import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Container } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import NavBar from './components/NavBar';
import Dashboard from './pages/Dashboard';
import Departments from './pages/Departments';
import Semesters from './pages/Semesters';
import Teachers from './pages/Teachers';
import Subjects from './pages/Subjects';
import Rooms from './pages/Rooms';
import TeacherSubjects from './pages/TeacherSubjects';
import LabTeachers from './pages/LabTeachers';
import Timetable from './pages/Timetable';
import TimetableGenerator from './pages/TimetableGenerator';
import TeacherTimetable from './pages/TeacherTimetable';
import Login from './pages/Auth/Login';
import ProtectedRoute from './components/ProtectedRoute';
import DepartmentSelector from './components/DepartmentSelector';
import { DepartmentProvider, useDepartment } from './context/DepartmentContext';
import { AuthProvider } from './contexts/AuthContext';

// Protected route component that requires department selection
const DepartmentProtectedRoute = ({ children, path }) => {
  const { selectedDepartment } = useDepartment();
  const location = useLocation();
  
  if (!selectedDepartment) {
    // Redirect to department selector with return path
    return <DepartmentSelector targetRoute={path} />;
  }
  
  return children;
};

// Department selection route component
const SelectDepartmentRoute = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const returnPath = queryParams.get('return') || '/';
  
  return <DepartmentSelector targetRoute={returnPath} />;
};

// Page transition animation wrapper
const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 15 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -15 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
};

function App() {
  const location = useLocation();
  
  return (
    <AuthProvider>
      <DepartmentProvider>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          
          {/* Protected routes - require authentication */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <PageTransition>
                    <Dashboard />
                  </PageTransition>
                </Container>
              </>
            } />
            
            <Route path="/departments" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <PageTransition>
                    <Departments />
                  </PageTransition>
                </Container>
              </>
            } />
            
            {/* Department selection route */}
            <Route path="/select-department" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <PageTransition>
                    <SelectDepartmentRoute />
                  </PageTransition>
                </Container>
              </>
            } />
            
            {/* Routes that require department selection */}
            <Route path="/semesters" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <DepartmentProtectedRoute path="/semesters">
                    <PageTransition>
                      <Semesters />
                    </PageTransition>
                  </DepartmentProtectedRoute>
                </Container>
              </>
            } />
            
            <Route path="/teachers" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <DepartmentProtectedRoute path="/teachers">
                    <PageTransition>
                      <Teachers />
                    </PageTransition>
                  </DepartmentProtectedRoute>
                </Container>
              </>
            } />
            
            <Route path="/subjects" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <DepartmentProtectedRoute path="/subjects">
                    <PageTransition>
                      <Subjects />
                    </PageTransition>
                  </DepartmentProtectedRoute>
                </Container>
              </>
            } />
            
            <Route path="/teacher-subjects" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <DepartmentProtectedRoute path="/teacher-subjects">
                    <PageTransition>
                      <TeacherSubjects />
                    </PageTransition>
                  </DepartmentProtectedRoute>
                </Container>
              </>
            } />
            
            <Route path="/lab-teachers" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <DepartmentProtectedRoute path="/lab-teachers">
                    <PageTransition>
                      <LabTeachers />
                    </PageTransition>
                  </DepartmentProtectedRoute>
                </Container>
              </>
            } />
            
            <Route path="/rooms" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <PageTransition>
                    <Rooms />
                  </PageTransition>
                </Container>
              </>
            } />
            
            <Route path="/timetable" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <PageTransition>
                    <Timetable />
                  </PageTransition>
                </Container>
              </>
            } />
            
            <Route path="/timetable-generator" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <PageTransition>
                    <TimetableGenerator />
                  </PageTransition>
                </Container>
              </>
            } />
            
            <Route path="/teacher-timetable" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <PageTransition>
                    <TeacherTimetable />
                  </PageTransition>
                </Container>
              </>
            } />
            
            <Route path="/teacher-timetable/:teacherId" element={
              <>
                <NavBar />
                <Container maxWidth="lg" style={{ marginTop: '20px' }}>
                  <PageTransition>
                    <TeacherTimetable />
                  </PageTransition>
                </Container>
              </>
            } />
          </Route>
          
          {/* Redirect any unmatched routes to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </AnimatePresence>
      </DepartmentProvider>
    </AuthProvider>
  );
}

export default App;
